import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/apiClient";
import type {
  SettlementGroup,
  CourierOverviewRow,
  CourierTodayShift,
  VehicleType,
} from "../api/types";
import { Chip } from "../components/ui/Chip";
import { EmptyState } from "../components/ui/EmptyState";
import { SegmentedControl } from "../components/ui/SegmentedControl";
import { ruleSummary } from "../utils/payoutRule";

const VEHICLE_LABELS: Record<VehicleType, string> = {
  BIKE: "Bike",
  MOTORBIKE: "Motorbike",
  CAR: "Car",
  VAN: "Van",
};

type VehicleFilter = "ALL" | VehicleType;
type AvailabilityFilter = "all" | "available" | "off";

/** G-2 schedule column: today's plan, with PLANNED as the no-show signal. */
function todayShiftBadge(shift: CourierTodayShift | null) {
  if (!shift) return { label: "Off today", className: "badge muted" };
  switch (shift.status) {
    case "CONFIRMED":
      return { label: "Confirmed today", className: "badge success" };
    case "PLANNED":
      return { label: "Planned — unconfirmed", className: "badge warning" };
    case "ENDED":
      return { label: "Day ended", className: "badge muted" };
    case "MISSED":
      return { label: "Missed today", className: "badge danger" };
    default:
      return { label: "Off today", className: "badge muted" };
  }
}

/**
 * G-1: the owner's "Manage couriers" page — every courier with vehicle,
 * availability, live carry vs cap, payout rate, and unpaid balance in one
 * place (FINALIZING.md G-1; schedule column arrives with G-2).
 */
export function CouriersPage() {
  const [rows, setRows] = useState<CourierOverviewRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [vehicle, setVehicle] = useState<VehicleFilter>("ALL");
  const [availability, setAvailability] = useState<AvailabilityFilter>("all");
  // G-6: unpaid delivered jobs grouped per courier — settle a week at once.
  const [settlement, setSettlement] = useState<SettlementGroup[] | null>(null);
  const [settling, setSettling] = useState<string | null>(null);
  const [settleError, setSettleError] = useState<string | null>(null);

  const refreshSettlement = useCallback(async () => {
    try {
      setSettlement(await api.getCourierSettlement());
      setSettleError(null);
    } catch (err: unknown) {
      setSettleError(
        err instanceof Error ? err.message : "Could not load what is owed.",
      );
    }
  }, []);

  useEffect(() => {
    void refreshSettlement();
  }, [refreshSettlement]);

  // The per-courier "Unpaid" figure on the cards comes from the overview,
  // so it is reloaded together with the settlement list.
  const refreshOverview = useCallback(async () => {
    try {
      setRows(await api.couriersOverview());
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not load couriers.");
    }
  }, []);

  const settleAll = async (group: SettlementGroup) => {
    setSettling(group.courier.id);
    setSettleError(null);
    try {
      await api.settleCourierJobs(group.jobs.map((j) => j.id));
      await Promise.all([refreshSettlement(), refreshOverview()]);
    } catch (err: unknown) {
      // The server refuses the whole group (one transaction) and names the
      // job; reload so the page shows the true state — a job settled from
      // elsewhere meanwhile must not be resubmitted from a stale list.
      const message =
        err instanceof Error ? err.message : "Could not mark those paid.";
      await Promise.all([refreshSettlement(), refreshOverview()]);
      setSettleError(message);
    } finally {
      setSettling(null);
    }
  };

  useEffect(() => {
    void refreshOverview();
  }, [refreshOverview]);

  const filtered = useMemo(() => {
    if (!rows) return null;
    return rows.filter((row) => {
      if (vehicle !== "ALL" && row.vehicleType !== vehicle) return false;
      if (availability === "available" && !(row.isAvailable && row.isActive)) {
        return false;
      }
      if (availability === "off" && row.isAvailable && row.isActive) return false;
      return true;
    });
  }, [rows, vehicle, availability]);

  return (
    <section className="page-stack">
      <div className="page-header">
        <div>
          <h2 className="page-title">Couriers</h2>
          <p className="page-subtitle">
            The whole fleet: vehicle, availability, carrying load, payout rate,
            and what each courier is still owed.
          </p>
        </div>
        <Link to="/app/payout-rules" className="button tinted">
          Payout rules
        </Link>
      </div>

      {error ? <div className="alert error">{error}</div> : null}
      {/* Outside the settlement block on purpose: a refusal such as "already
          marked paid — reload the list" reloads the list, which may then be
          EMPTY, and the explanation must survive that (pre-release fix
          2026-09-09, seen in the browser walk). */}
      {settleError ? <div className="alert error">{settleError}</div> : null}

      {/* G-6: the settlement table — what is owed to whom, settled per
          courier in one tap. Each job keeps its own event row server-side. */}
      {settlement && settlement.length > 0 ? (
        <div className="card" style={{ display: "grid", gap: "var(--space-3)" }}>
          <h3 style={{ margin: 0 }}>Owed to couriers</h3>
          {settlement.map((group) => (
            <div key={group.courier.id} className="courier-settlement-group">
              <div className="day-shifts-row-head">
                <strong>
                  {group.courier.fullName}
                  <span className="muted">
                    {" "}
                    · {group.jobCount}{" "}
                    {group.jobCount === 1 ? "delivery" : "deliveries"}
                  </span>
                </strong>
                <span>
                  <strong>{group.totalEur} €</strong>{" "}
                  <button
                    type="button"
                    className="button tinted"
                    disabled={settling === group.courier.id}
                    onClick={() => void settleAll(group)}
                  >
                    {settling === group.courier.id
                      ? "Marking…"
                      : "Mark all paid"}
                  </button>
                </span>
              </div>
              <ul className="courier-settlement-jobs">
                {group.jobs.map((job) => (
                  <li key={job.id} className="muted">
                    {job.deliveredAt
                      ? new Date(job.deliveredAt).toLocaleDateString()
                      : "—"}{" "}
                    · {job.code} · {job.deliveryAddress} —{" "}
                    {job.payoutEur ?? "—"} €
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      ) : null}

      <div className="card courier-filters">
        <div className="button-row">
          {(["ALL", "BIKE", "MOTORBIKE", "CAR", "VAN"] as const).map((option) => (
            <Chip
              key={option}
              label={option === "ALL" ? "All vehicles" : VEHICLE_LABELS[option]}
              selected={vehicle === option}
              onClick={() => setVehicle(option)}
            />
          ))}
        </div>
        <SegmentedControl
          ariaLabel="Availability"
          value={availability}
          onChange={setAvailability}
          options={[
            { value: "all", label: "Everyone" },
            { value: "available", label: "Available now" },
            { value: "off", label: "Off / inactive" },
          ]}
        />
      </div>

      {!rows && !error ? <p className="muted">Loading…</p> : null}

      {filtered && filtered.length === 0 ? (
        <EmptyState
          title="No couriers match"
          message={
            rows && rows.length === 0
              ? "Create courier accounts from Team → New user."
              : "Loosen the filters to see the rest of the fleet."
          }
        />
      ) : null}

      {filtered?.map((row) => (
        <div key={row.id} className="card courier-row">
          <div className="day-shifts-row-head">
            <div className="courier-row-name">
              <span
                className={
                  row.isAvailable && row.isActive
                    ? "courier-dot is-on"
                    : "courier-dot"
                }
                aria-hidden="true"
              />
              <strong>{row.fullName}</strong>
              {!row.isActive ? <span className="badge muted">Inactive</span> : null}
              {(() => {
                const badge = todayShiftBadge(row.todayShift);
                return <span className={badge.className}>{badge.label}</span>;
              })()}
            </div>
            <span className="muted">
              {row.vehicleType ? VEHICLE_LABELS[row.vehicleType] : "—"}
              {row.vehiclePlate ? ` · ${row.vehiclePlate}` : ""}
            </span>
          </div>

          <p className="muted courier-row-contact">
            {row.email}
            {row.phoneNumber ? ` · ${row.phoneNumber}` : ""}
          </p>

          <div className="courier-row-stats">
            <span>
              Carrying <strong>{row.activeJobCount}</strong>
              {row.maxConcurrentJobs != null ? ` / ${row.maxConcurrentJobs}` : ""}
            </span>
            <span>
              Delivered <strong>{row.deliveredCount}</strong>
            </span>
            <span className={Number(row.unpaidPayoutEur) > 0 ? "courier-unpaid" : undefined}>
              Unpaid <strong>{row.unpaidPayoutEur} €</strong>
            </span>
          </div>

          <p className="muted courier-row-rate">{ruleSummary(row.payoutRule)}</p>
          {row.notes ? <p className="muted courier-row-rate">{row.notes}</p> : null}
          <div className="button-row">
            <Link
              to={`/app/couriers/${row.id}/schedule`}
              className="button plain"
            >
              Plan days
            </Link>
          </div>
        </div>
      ))}
    </section>
  );
}
